"use client";

import { useEffect, useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { KbdBadge } from "@/components/ui/KbdBadge";
import { type ShortcutCategory } from "@/lib/config/shortcuts";

const CATEGORY_ORDER: ShortcutCategory[] = ["painting", "grid", "history"];

const CATEGORY_KEYS: Record<ShortcutCategory, string> = {
  painting: "categoryPainting",
  grid: "categoryGrid",
  history: "categoryHistory",
};

interface OverlayRow {
  key: string;
  descriptionKey: string;
  descriptionParams?: Record<string, string | number>;
}

const ROWS: Record<ShortcutCategory, OverlayRow[]> = {
  painting: [
    { key: "1–9", descriptionKey: "selectAction", descriptionParams: { n: "1–9" } },
    { key: "E", descriptionKey: "eraser" },
    { key: "Wheel", descriptionKey: "wheel" },
  ],
  grid: [
    { key: "Space", descriptionKey: "autofill" },
  ],
  history: [
    { key: "Ctrl+Z", descriptionKey: "undo" },
    { key: "Ctrl+Y", descriptionKey: "redo" },
  ],
};

export function ShortcutsOverlay({
  open: controlledOpen,
  onOpenChange,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
} = {}) {
  const t = useTranslations("shortcuts");
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = useCallback(
    (v: boolean) => {
      if (onOpenChange) onOpenChange(v);
      if (!isControlled) setInternalOpen(v);
    },
    [isControlled, onOpenChange],
  );

  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const isInput =
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement;
      if (isInput) return;

      if (e.code === "KeyH") {
        e.preventDefault();
        toggle();
        return;
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, toggle, setOpen]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "var(--overlay-heavy)", backdropFilter: "blur(3px)" }}
      onClick={() => setOpen(false)}
      role="dialog"
      aria-label={t("title")}
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-lg border border-line-2 bg-panel shadow-2xl animate-fade-in"
        style={{ padding: "20px 24px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-fg">{t("title")}</h2>
          <div className="flex items-center gap-1.5 text-[10px] text-fg-mute">
            {t("closeHint", { key1: "H", key2: "Esc" })}
          </div>
        </div>

        <div className="space-y-4">
          {CATEGORY_ORDER.map((cat) => {
            const rows = ROWS[cat];
            return (
              <div key={cat}>
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-fg-mute">
                  {t(CATEGORY_KEYS[cat])}
                </div>
                <div className="space-y-1">
                  {rows.map((row) => (
                    <div
                      key={row.descriptionKey}
                      className="flex items-center justify-between py-0.5"
                    >
                      <span className="text-xs text-fg-dim">
                        {t(row.descriptionKey, row.descriptionParams)}
                      </span>
                      <KbdBadge className="ml-3 shrink-0">{row.key}</KbdBadge>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
